import {
  HttpBackend,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { ConfigService } from './config.service';
import { FeatureFlags } from './feature-flags/feature-flags';
import { LaunchDarklyService } from './feature-flags/launchdarkly.service';
import { LwaClientService } from './lwa-client.service';

@Injectable()
export class LwaAuthInterceptor implements HttpInterceptor {
  constructor(
    private lwaClientService: LwaClientService,
    private configService: ConfigService,
    private httpBackend: HttpBackend,
    private launchDarklyService: LaunchDarklyService,
    private router: Router,
  ) {}

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const token = this.lwaClientService.getToken();

    // No LWA token — pass through to next interceptor (Auth0)
    if (!token) {
      return next.handle(request);
    }

    // Not targeting the API server — pass through
    if (!request.url.startsWith(this.configService.json.myoneServer)) {
      return next.handle(request);
    }

    // Check feature flag
    return this.launchDarklyService.featureFlag$(FeatureFlags.LWA_NATIVE_LOGIN, false).pipe(
      take(1),
      switchMap(flagEnabled => {
        if (!flagEnabled) {
          return next.handle(request);
        }

        return this.sendWithLwaToken(request, token);
      }),
    );
  }

  private sendWithLwaToken<T>(request: HttpRequest<T>, token: string): Observable<HttpEvent<T>> {
    const authenticatedRequest = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
      withCredentials: true,
    });

    return this.httpBackend.handle(authenticatedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handleUnauthorized(request);
        }
        return observableThrowError(() => error);
      }),
    );
  }

  private handleUnauthorized<T>(originalRequest: HttpRequest<T>): Observable<HttpEvent<T>> {
    return from(this.lwaClientService.attemptRefresh()).pipe(
      switchMap(newToken => {
        if (newToken) {
          // Retry with the new token
          const retryRequest = originalRequest.clone({
            headers: originalRequest.headers.set('Authorization', `Bearer ${newToken}`),
            withCredentials: true,
          });
          return this.httpBackend.handle(retryRequest);
        }

        // Refresh failed — redirect to LWA login
        this.router.navigate(['/lwa-login'], {
          queryParams: { sessionExpired: 'true' },
        });
        return observableOf(null as any);
      }),
    );
  }
}
